import React, { useState, FunctionComponent, useEffect, useRef, useMemo, useCallback, CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Presentation } from './presentation';
import { fetchScoutableRecruitments, postScout, fetchScoutUsageCounts } from '../../../services/scouts';
import { fetchPersonHistories } from '../../../services/personProfiles';
import { ScoutModalHistoryKey, ScoutModalState } from './types';
import { getCsrfToken } from '../../../libs/csrfToken';
import { containsScoutNgWord } from '../../../libs/scoutMessageValidation';
import type { ScoutUsageCounts } from '../../../types/scoutPage';

type Props = {
  isAllowedToScout: boolean;
  isMinimum: boolean;
  icon: string;
  personId: number;
  candidateSummary?: React.ReactNode;
  onLike?: (event: React.MouseEvent<HTMLElement>) => void;
  isLiked?: boolean;
  isBookmarked: boolean;
  isAlreadyConnected: boolean;
  messageBoxId?: number | null;
  handleCreateBookmark?: () => void;
  handleDeleteBookmark?: () => void;
  personIsScouted: () => void;
  handleModalClose: () => void;
  insertNotification: (insertMessage: string, type?: string) => void;
  handleUsageCountsChange?: (counts: ScoutUsageCounts) => void;
  outOfLike?: boolean;
  outOfScout?: boolean;
  style: CSSProperties;
};

export const Component: FunctionComponent<Props> = ({
  isAllowedToScout,
  isMinimum,
  icon,
  personId,
  candidateSummary,
  onLike,
  isLiked,
  isBookmarked,
  isAlreadyConnected,
  messageBoxId,
  handleCreateBookmark,
  handleDeleteBookmark,
  personIsScouted,
  handleModalClose,
  insertNotification,
  handleUsageCountsChange,
  outOfLike,
  outOfScout,
  style,
}) => {
  const [state, setState] = useState<ScoutModalState>({
    isModalOpen: false,
    scoutableRecruitments: [],
    unscoutableRecruitments: [],
    enteredRecruitments: [],
    targetRecruitment: null,
    personId: null,
    body: '',
    favorites: [],
    likes: [],
    scouts: [],
    openHistories: [],
    isRecruitmentSelectOpen: false,
    recruitmentKeyword: '',
  });
  const recruitmentSelectRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const selectedRecruitment = state.scoutableRecruitments.find(recruitment => recruitment[0] === state.targetRecruitment);
  const visibleRecruitments = useMemo(() => {
    const keyword = state.recruitmentKeyword.trim().toLocaleLowerCase();
    if (!keyword) return state.scoutableRecruitments;

    return state.scoutableRecruitments.filter(([, title]) => title.toLocaleLowerCase().includes(keyword));
  }, [state.recruitmentKeyword, state.scoutableRecruitments]);

  useEffect(() => {
    const { body, documentElement: html } = document;
    const originalBodyOverflow = body.style.overflow;
    const originalHtmlOverflow = html.style.overflow;
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    return () => {
      body.style.overflow = originalBodyOverflow;
      html.style.overflow = originalHtmlOverflow;
    };
  }, []);

  useEffect(() => {
    const request = async () => {
      try {
        const [response, histories] = await Promise.all([
          fetchScoutableRecruitments(personId),
          fetchPersonHistories(personId),
        ]);
        setState(prev => ({
          ...prev,
          scoutableRecruitments: response.scoutable_recruitments,
          unscoutableRecruitments: response.unscoutable_recruitments,
          enteredRecruitments: response.entered_recruitments,
          favorites: histories.favorites,
          likes: histories.likes || [],
          scouts: histories.scouts,
        }));
      } catch (error) {
        console.log(error)
      }
    };
    request();
  }, [personId]);

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const targetArea = state.body

    if (!state.targetRecruitment) {
      insertNotification('スカウトする求人を選択してください。', 'danger');
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return
    } else if (!targetArea) {
      insertNotification('スカウトメッセージを記入してください。', 'danger');
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return
    } else if (containsScoutNgWord(targetArea)) {
      insertNotification('スカウトメッセージに使用できない文字列が含まれています。', 'danger');
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      return
    }

    const body = JSON.stringify({
      body: targetArea,
      personId: state.personId,
      targetRecruitment: state.targetRecruitment
    });
    try {
      await postScout(body, getCsrfToken());
      fetchScoutUsageCounts()
        .then(usageCounts => handleUsageCountsChange?.(usageCounts))
        .catch(error => console.log(error));
      handleModalClose();
      personIsScouted();
      insertNotification('スカウトメールを送信しました！応募を待ちましょう！', 'success');
    } catch (error) {
      console.log(error);
      insertNotification('スカウト送信でエラーが発生しました。', 'danger');
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const selectRecruitment = (recruitmentId: number) => {
    setState(prev => ({
      ...prev,
      targetRecruitment: recruitmentId,
      personId,
      isRecruitmentSelectOpen: false,
      recruitmentKeyword: '',
    }));
  };
  const toggleRecruitmentSelect = () => {
    setState(prev => ({
      ...prev,
      isRecruitmentSelectOpen: !prev.isRecruitmentSelectOpen,
      recruitmentKeyword: prev.isRecruitmentSelectOpen ? '' : prev.recruitmentKeyword,
    }));
  };
  const closeRecruitmentSelect = useCallback(() => {
    setState(prev => ({
      ...prev,
      isRecruitmentSelectOpen: false,
      recruitmentKeyword: '',
    }));
  }, []);

  useEffect(() => {
    if (!state.isRecruitmentSelectOpen) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (!recruitmentSelectRef.current?.contains(event.target as Node)) {
        closeRecruitmentSelect();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [closeRecruitmentSelect, state.isRecruitmentSelectOpen]);
  const changeRecruitmentKeyword = (keyword: string) => {
    setState(prev => ({
      ...prev,
      recruitmentKeyword: keyword,
    }));
  };
  const changeMessageBody = (message: string) => {
    setState(prev => ({
      ...prev,
      body: message,
    }));
  };
  const toggleHistory = (target: ScoutModalHistoryKey) => {
    setState(prev => ({
      ...prev,
      openHistories: prev.openHistories.includes(target)
        ? prev.openHistories.filter(history => history !== target)
        : [...prev.openHistories, target],
    }));
  };
  const modal = (
    <Presentation
      icon={icon}
      personId={personId}
      candidateSummary={candidateSummary}
      onLike={onLike}
      isLiked={isLiked}
      isBookmarked={isBookmarked}
      isAlreadyConnected={isAlreadyConnected}
      messageBoxId={messageBoxId}
      isAllowedToScout={isAllowedToScout}
      style={style}
      scoutableRecruitments={state.scoutableRecruitments}
      favorites={state.favorites}
      likes={state.likes}
      scouts={state.scouts}
      openHistories={state.openHistories}
      targetRecruitment={state.targetRecruitment}
      selectedRecruitment={selectedRecruitment}
      visibleRecruitments={visibleRecruitments}
      isRecruitmentSelectOpen={state.isRecruitmentSelectOpen}
      recruitmentKeyword={state.recruitmentKeyword}
      messageBody={state.body}
      recruitmentSelectRef={recruitmentSelectRef}
      isSubmitting={isSubmitting}
      handleModalClose={handleModalClose}
      handleSubmit={handleSubmit}
      selectRecruitment={selectRecruitment}
      toggleRecruitmentSelect={toggleRecruitmentSelect}
      changeRecruitmentKeyword={changeRecruitmentKeyword}
      changeMessageBody={changeMessageBody}
      toggleHistory={toggleHistory}
      handleCreateBookmark={handleCreateBookmark}
      handleDeleteBookmark={handleDeleteBookmark}
      outOfLike={outOfLike}
      outOfScout={outOfScout}
    />
  );

  if (typeof document === 'undefined') return modal;

  return createPortal(modal, document.body);
}
